// src/services/geminiService.ts
// Service untuk berkomunikasi dengan Google Gemini API (menggunakan SDK)

import { GoogleGenerativeAI } from "@google/generative-ai";
import { GeminiRequest, GeminiResponse } from "../types/chatTypes";

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";

// Client-side throttle: minimal 4 detik antar request
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 4000;

/**
 * System prompt yang mendefinisikan persona chatbot EcoNoise
 */
const SYSTEM_PROMPT = `Kamu EcoNoise Assistant — ahli kebisingan (noise pollution).
Jawab dalam Bahasa Indonesia, singkat & informatif. Gunakan data dari konteks, jangan mengarang angka.
Bisa bantu: statistik wilayah, dampak kesehatan, regulasi, tips kurangi kebisingan.
Jika di luar topik, arahkan kembali dengan sopan.`;

// Initialize Gemini AI
let genAI: GoogleGenerativeAI | null = null;
let model: any = null;

function initializeGemini() {
  if (!GEMINI_API_KEY) {
    console.warn("⚠️ GEMINI_API_KEY not found in environment variables");
    return;
  }

  try {
    genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
    model = genAI.getGenerativeModel({
      model: "gemini-2.5-flash",
      generationConfig: {
        temperature: 0.7,
        topP: 0.95,
        topK: 40,
        maxOutputTokens: 512,
      },
      systemInstruction: SYSTEM_PROMPT,
    });
    console.log("✅ Gemini service initialized (gemini-2.5-flash)");
  } catch (error) {
    console.error("❌ Failed to initialize Gemini:", error);
  }
}

// Initialize on module load
initializeGemini();

/**
 * Helper: delay/sleep
 */
function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mengirim request ke Gemini API dengan retry otomatis untuk rate limit (429)
 */
export async function sendToGemini(request: GeminiRequest): Promise<GeminiResponse> {
  if (!model || !genAI) {
    return {
      text: "API key Gemini belum dikonfigurasi. Silakan tambahkan REACT_APP_GEMINI_API_KEY di file .env",
      success: false,
      error: "Missing API key or model not initialized",
    };
  }

  // Client-side throttle — tunggu jika terlalu cepat
  const now = Date.now();
  const elapsed = now - lastRequestTime;
  if (elapsed < MIN_REQUEST_INTERVAL) {
    await delay(MIN_REQUEST_INTERVAL - elapsed);
  }
  lastRequestTime = Date.now();

  // Bangun prompt dengan context dan history
  let prompt = "";

  // Add context if available
  if (request.context) {
    prompt += `[Konteks Data]\n${request.context}\n\n`;
  }

  // Add conversation history
  if (request.history && request.history.length > 0) {
    prompt += "Riwayat percakapan:\n";
    for (const msg of request.history) {
      const role = msg.role === "assistant" ? "model" : "user";
      if (role === "model") {
        // Skip leading model messages
        if (!prompt.includes("User:")) continue;
        prompt += `Asisten: ${msg.content}\n`;
      } else {
        prompt += `User: ${msg.content}\n`;
      }
    }
    prompt += "\n";
  }

  // Add current user message
  prompt += `[Pertanyaan User]\n${request.prompt}\n\nAsisten:`;

  const MAX_RETRIES = 3;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await model.generateContent(prompt);
      const response = result.response;
      const text = response.text();

      return { text, success: true };
    } catch (error: any) {
      console.error(`Gemini service error (attempt ${attempt + 1}):`, error);

      // Check for rate limit error
      const isRateLimit = error?.message?.includes("429") || error?.message?.includes("rate");

      if (isRateLimit && attempt < MAX_RETRIES) {
        const waitMs = Math.pow(2, attempt + 1) * 1000; // 2s, 4s, 8s
        console.warn(`Gemini rate limited. Retrying in ${waitMs / 1000}s... (attempt ${attempt + 1}/${MAX_RETRIES})`);
        await delay(waitMs);
        continue;
      }

      if (attempt < MAX_RETRIES) {
        await delay(Math.pow(2, attempt + 1) * 1000);
        continue;
      }

      // Final attempt failed
      if (isRateLimit) {
        return {
          text: "Maaf, API sedang sibuk (rate limit). Tunggu beberapa detik lalu coba lagi. ⏳",
          success: false,
          error: error.message,
        };
      }

      return {
        text: "Maaf, terjadi kesalahan saat menghubungi AI. Silakan coba lagi.",
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  return {
    text: "Maaf, gagal menghubungi AI setelah beberapa percobaan. Silakan coba lagi nanti.",
    success: false,
    error: "Max retries exceeded",
  };
}

/**
 * Test koneksi ke Gemini API menggunakan SDK
 * Bisa dipanggil dari console: await window.testGeminiAPI()
 */
export async function testGeminiConnection(): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> {
  console.log("🧪 Testing Gemini API connection...");
  console.log("📌 API Key:", GEMINI_API_KEY ? `${GEMINI_API_KEY.slice(0, 20)}...` : "NOT FOUND");
  console.log("🔧 Model:", model ? "gemini-2.5-flash" : "NOT INITIALIZED");

  if (!model || !genAI) {
    return {
      success: false,
      message: "❌ API key tidak ditemukan atau model belum diinisialisasi. Periksa .env file.",
    };
  }

  try {
    console.log("📤 Sending test request...");
    
    const result = await model.generateContent("Test koneksi, jawab singkat saja");
    const response = result.response;
    const text = response.text();
    
    console.log("✅ Response received:", text);
    console.log("✅ Full response:", response);

    return {
      success: true,
      message: `✅ Koneksi berhasil! Model: gemini-2.5-flash\nResponse: "${text}"`,
      details: { text, response },
    };
  } catch (error: any) {
    console.error("❌ Connection test failed:", error);
    return {
      success: false,
      message: `❌ Error: ${error?.message || "Unknown error"}`,
      details: error,
    };
  }
}

// Expose ke window untuk testing dari console
if (typeof window !== "undefined") {
  (window as any).testGeminiAPI = testGeminiConnection;
  console.log("💡 Untuk test API, ketik di console: await window.testGeminiAPI()");
}
